import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePayment } from './usePayment';

const notifyPaymentSuccessToOpener = (paymentId: string | null) => {
  try {
    if (window.opener) {
      window.opener.postMessage(
        {
          type: 'PAYMENT_SUCCESS',
          feature: 'bettie',
          data: {
            paymentId
          }
        },
        window.location.origin
      );
    }
  } catch {
    // noop
  }
};

export const usePaymentSuccess = () => {
  const navigate = useNavigate();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { approvePaymentFromSearchParams } = usePayment();

  const queryParams = new URLSearchParams(window.location.search);
  const paymentType = queryParams.get('paymentType');
  const provider = queryParams.get('provider');
  const payLetterPaymentId = queryParams.get('paymentId');

  const handleApprovePayment = useCallback(async () => {
    try {
      const ok = await approvePaymentFromSearchParams(new URLSearchParams(window.location.search));
      const paymentId = new URLSearchParams(window.location.search).get('paymentId');
      if (!ok) {
        setIsLoading(false);
        navigate('/payment/fail', { replace: true });
        return;
      }
      setIsSuccess(true);
      notifyPaymentSuccessToOpener(paymentId);
    } catch {
      navigate('/payment/fail', { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [navigate, approvePaymentFromSearchParams]);

  const handlePayLetterSuccess = useCallback(() => {
    setIsSuccess(true);
    setIsLoading(false);
    notifyPaymentSuccessToOpener(payLetterPaymentId);
  }, [payLetterPaymentId]);

  // 승인 호출: 최초 1회만 실행 (StrictMode 이중 호출 방지)
  const hasApprovedRef = useRef(false);
  useEffect(() => {
    if (hasApprovedRef.current) return;
    hasApprovedRef.current = true;
    if (provider === 'pay_letter') {
      handlePayLetterSuccess();
      return;
    }
    if (paymentType === 'subscription') {
      setIsSuccess(true);
      setIsLoading(false);
    } else {
      void handleApprovePayment();
    }
  }, [handleApprovePayment, handlePayLetterSuccess, paymentType, provider]);

  // 성공 시 2초 후 창 닫기
  useEffect(() => {
    if (!isSuccess) return;
    const timer = setTimeout(() => {
      window.close();
    }, 2000);
    return () => clearTimeout(timer);
  }, [isSuccess]);

  return { isLoading, isSuccess };
};

export default usePaymentSuccess;
